import { useCallback, useMemo } from 'react';
import type { FormEventHandler } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { useDraftStepSubmitBus } from './contexts/DraftStepSubmitBus';
import { FieldFactory } from './FieldFactory';
import { useDraftStepData } from './hooks/useDraftStepData';
import { useDraftStepFields } from './hooks/useDraftStepFields';
import { useDraftStepProgress } from './hooks/useDraftStepProgress';
import { useDraftStepSubmit } from './hooks/useDraftStepSubmit';
import { useStepFormExtensions } from './hooks/useStepFormExtension';
import { parseFormStepError } from './utils/formStepErrorParser';

export const FormStep = () => {
  const { next, previous, currentStep, isStepValidated } =
    useDraftStepProgress();
  const { fields } = useDraftStepFields(currentStep);
  const submitPayoutStep = useDraftStepSubmit(currentStep);
  const currentStepData = useDraftStepData(currentStep);
  const { emitSuccessfulSubmit } = useDraftStepSubmitBus();
  const notification = useOpenNotification();

  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      [currentStep]: currentStepData
    }
  });

  const {
    control,
    trigger,
    formState: { isDirty, dirtyFields },
    handleSubmit,
    setError
  } = useMemo(() => form, [form]);

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (evt) => {
      evt.preventDefault();
      // Check if required fields have been filled
      const ok = await trigger();
      if (!ok) {
        return;
      }

      // If form has values changed or if values are not validated, submit them. Else just move to next.
      if (
        (isDirty && Object.keys(dirtyFields).length !== 0) ||
        isStepValidated === false
      ) {
        try {
          await handleSubmit(submitPayoutStep)(evt)
            .then(() => emitSuccessfulSubmit(currentStep))
            .then(next);
        } catch (e) {
          const parsedErrors = parseFormStepError(e, currentStep);

          if (parsedErrors.ok === false) {
            notification({
              kind: 'error',
              key: 'create-manual-payout-step-error',
              message:
                'An error occurred. Please validate that the data is correct.'
            });
            return;
          }
          parsedErrors.fields.forEach((field) => {
            setError(field.fullName, {
              message: field.message
            });
          });
        }
      } else {
        next();
      }
    },
    [
      trigger,
      isDirty,
      dirtyFields,
      isStepValidated,
      handleSubmit,
      submitPayoutStep,
      next,
      emitSuccessfulSubmit,
      currentStep,
      notification,
      setError
    ]
  );

  const { StartOfFormSection, EndOfFormSection } =
    useStepFormExtensions(currentStep);

  return (
    <div className="space-y-5">
      <FormProvider {...form}>
        <form
          autoComplete="off"
          className="w-full space-y-3"
          onSubmit={onSubmit}
          data-testid="new-manual-payout-form"
        >
          {StartOfFormSection && <StartOfFormSection step={currentStep} />}
          {fields.map((field) => {
            return (
              <Controller
                key={field.fullName}
                name={field.fullName}
                control={control}
                rules={{
                  required: field.optional
                    ? false
                    : `${field.label} is required`,
                  // @ts-expect-error value for validate is always a string in our case
                  validate: field.validate
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => (
                  <>
                    <FieldFactory
                      field={field}
                      onChange={onChange}
                      value={value}
                      className="h-10"
                    />
                    {error && (
                      <span className="text-xs text-red-500/75">
                        {error.message}
                      </span>
                    )}
                  </>
                )}
              />
            );
          })}
          {EndOfFormSection && <EndOfFormSection step={currentStep} />}
          <div className="grid h-9 grid-cols-2 gap-4">
            <SecondaryButton onClick={previous}>Previous</SecondaryButton>
            <PrimaryButton
              htmlType="submit"
              data-testid="new-manual-payout-next-button"
            >
              Next
            </PrimaryButton>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
